import { StateCreator } from "zustand";
import {
	OrganizationEntityType,
	ProfileEntityType,
	SupabaseTableEnum,
	ClientEntityType,
} from "../../../../lib/supabase/supabaseTypes";
import { supabase } from "../../../../lib/supabase/supabaseClient";
import { Logger } from "../../../../lib/logger/Logger";
import { useCentralStore } from ".";
import { getClientAvatar } from "../../pages/Chat/utils";
import { Member } from "@/dentlab/src/store/UserManagement/user-management.store";
import { getRole } from "@/dentlab/src/lib/util";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { useRealtimeStore } from "@/lib/store/Realtime";

export interface CentralSlice {
	userId: string;
	userEmail: string;
	role:  0 | 1 | 2 | null;
	profile: ProfileEntityType | null;
	avatarUrl: string;
	organization: OrganizationEntityType | null;
	membersLookup: Record<string, Member>;
	activeUsers: Member[];
	clientsLookup: Record<string, ClientEntityType>;
	initialize: () => Promise<void>;
	getOrganization: () => Promise<void>;
	getSession: () => Promise<void | string>;
	getProfile: (userId: string) => Promise<void>;
	signOut: () => Promise<void>;
	getMembers: () => Promise<void>;
	getClients: () => Promise<void>;
}

export const createCentralStore: StateCreator<CentralSlice> = (set, get) => ({
	profile: null,
	userId: "",
	userEmail: "",
	role: null,
	organization: null,
	avatarUrl: "",
	membersLookup: {},
	activeUsers: [],
	clientsLookup: {},
	initialize: async () => {
		const { getSession, getProfile, getOrganization, getMembers, getClients } =
			useCentralStore.getState();

		const userId = await getSession();
		if (!userId) return;
		await getProfile(userId);
		await getOrganization();
		await getMembers();
		await getClients();
		await useRealtimeStore.getState().getProfilesForChat();
	},

	getSession: async () => {
		Logger.log("[Sign in] Retrieving session");
		try {
			const { data } = await supabase.auth.getSession();


			if (!data.session) {
				Logger.error("No session found");
				return;
			}

			const { user: userInformation } = data.session;
			const { id: userId, email: userEmail, app_metadata: appMetaData } = userInformation;
			
			const { role } = appMetaData;

			set({
				userId,
				userEmail,
				role,
			});
			return userId;
		} catch (error) {
			Logger.error(error, {}, "Error retrieving session");
		}
	},

	getProfile: async (userId: string) => {
		const { data: profile, error } = await supabase
			.from("profiles")
			.select()
			.eq("id", userId)
			.single();

		if (error) {
			Logger.error(error, {}, "[Sign in] Error getting profile");
			await get().signOut();
			return;
		}

		if (!profile) {
			Logger.error("[Sign in] No profile found");
			await get().signOut();
			return;
		}
		if (profile.avatar_url && profile.organization_id) {
			getClientAvatar(profile.avatar_url, profile.organization_id)
				.then(url => url && set({ avatarUrl: url }))
				.catch(() => set({ avatarUrl: "" }));
		}

		Logger.log("[Sign in] Profile", profile);
		set({
			profile,
		});
	},

	getOrganization: async () => {
		const profile = get().profile;
		if (!profile || !profile.organization_id) return;

		const { data, error } = await supabase
			.from("organizations")
			.select()
			.eq("id", profile?.organization_id)
			.single();

		if (error) {
			Logger.error(error);
			return;
		}
		if (data) {
			set({
				organization: data,
			});
		}
	},

	signOut: async () => {
		await supabase.auth.signOut();
		set({
			profile: null,
		});
	},
	getMembers: async () => {
		const { organization, role, profile } = useCentralStore.getState();

		if (!organization || !profile) {
			Logger.error("No Organization set");
			return;
		}
		const organizationId = organization.id;
		const { data, error } = await supabase
			.from(SupabaseTableEnum.PROFILES_TO_INVITES_VIEW)
			.select()
			.or(
				`organization_id.eq.${organizationId},allowed_signups_lab_organization_id.eq.${organizationId}`
			);

		if (error) {
			Logger.error(error);
			return;
		}

		const { data: emptyProfilesData, error: emptyProfilesError } =
			await supabase
				.from(SupabaseTableEnum.EMPTY_PROFILES)
				.select()
				.eq("organization_id", organizationId);

		if (emptyProfilesError) {
			Logger.error(emptyProfilesError);
			return;
		}

		const fullProfiles = data.map((d) => ({
			id: d.id ?? d.allowed_signups_lab_id,
			first_name: d.first_name ?? "",
			last_name: d.last_name ?? "",
			code: d.code ?? "-",
			email: d.email ?? d.allowed_signups_lab_email,
			invited: d.allowed_signups_lab_created_at
				? new Date(d.allowed_signups_lab_created_at).toLocaleDateString(
						"de-ch"
					)
				: "-",
			role:
				d.id == profile.id
					? getRole(role as number)
					: getRole(d.allowed_signups_lab_role as number),
			accepted:
				d.email && d.email == d.allowed_signups_lab_email
					? new Date(d.created_at ?? "").toLocaleDateString("de-ch")
					: "-",
			deactivated_at: d.deactivated_at,
		}));

		const emptyProfiles = emptyProfilesData.map((d) => ({
			id: d.id,
			first_name: d.first_name ?? "",
			last_name: d.last_name ?? "",
			email: "",
			invited: "-",
			role: "Mitarbeiter ohne Profil",
			accepted: "-",
			code: d.code ?? "",
			deactivated_at: d.deactivated_at,
		}));

		set({
			membersLookup: {
				...fullProfiles.reduce((acc, curr) => {
					if (
						!curr.id ||
						(curr.email?.includes("support-") &&
							curr.email?.includes("@dentdesk.ch"))
					) {
						return acc;
					}
					acc[curr.id] = curr;
					return acc;
				}, {} as any),
				...emptyProfiles.reduce(
					(acc, curr) => {
						if (!curr.id) {
							return acc;
						}
						acc[curr.id] = curr;
						return acc;
					},
					{} as Record<string, Member>
				),
			},
			activeUsers: [
				...fullProfiles
					.filter(
						(member) =>
							!member.deactivated_at &&
							!(member.email?.includes("@dentdesk.ch") &&
							member.email?.includes("support-"))
					)
					.map((member) => ({
						...member,
						id: member.id?.toString() || undefined,
					})),
				...emptyProfiles
					.filter(
						(member) =>
							!member.deactivated_at
					)
					.map((member) => ({
						...member,
						id: member.id?.toString() || undefined,
					})),
			],
		});
	},
	getClients: async () => {
		const { organization } = useCentralStore.getState();
		if (!organization) {
			Logger.error("No Organization set");
			return;
		}
		const { data, error } = await supabase
        .from("clients")
        .select("*")
        .eq("organization_id", organization.id)
        .order("created_at", { ascending: false });
        
		if (error || !data) {
			Logger.log("Error fetching clients", error);
			showNotification({
				message: "Fehler beim Laden der Auftraggeber",
				type: "error",
			});
			return;
		}

		set({
			clientsLookup: data.reduce((acc, curr) => {
				acc[curr.id] = curr;
				return acc;
			}, {} as Record<string, ClientEntityType>),
		});
	},
});
